/**
 * Related Item Card Component
 * Displays a related item with proper thumbnail handling and fallback gradient card
 * Uses new API response shape with display_title, has_thumbnail, is_text_item
 * Enhanced with better text display for fallback cards
 */

import React, { useState } from 'react';

/**
 * Helper function to extract first 8-12 words from text
 */
function getFirstWords(text, minWords = 8, maxWords = 12) {
  if (!text || typeof text !== 'string') return '';
  const words = text.trim().split(/\s+/);
  const wordCount = Math.min(Math.max(minWords, words.length), maxWords);
  return words.slice(0, wordCount).join(' ');
}

/**
 * Related Item Card
 * @param {Object} props
 * @param {Object} props.item - Related item object with new API shape
 * @param {Function} props.onClick - Click handler
 */
export default function RelatedItemCard({ item, onClick }) {
  const [imageError, setImageError] = useState(false);
  
  // Determine thumbnail URL - always use thumbnail_url if available
  const thumbnailUrl = item.thumbnail_url || (item.url && !item.is_text_item ? item.url : null);
  
  // Use API-provided has_thumbnail field, but also check if we have a valid URL
  const hasThumbnail = !imageError && item.has_thumbnail && !!thumbnailUrl && thumbnailUrl.trim().length > 0;
  const isTextItem = item.is_text_item;
  const displayTitle = item.display_title || 'Untitled';
  
  // For fallback cards, show first 8-12 words
  const fallbackText = getFirstWords(displayTitle, 8, 12);

  const handleClick = (e) => {
    e.stopPropagation();
    onClick();
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <button
      onClick={handleClick}
      className="w-full aspect-square rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
      style={{
        borderRadius: '16px',
      }}
      aria-label={displayTitle}
    >
      {hasThumbnail ? (
        // Show thumbnail image - always show if available
        <img
          src={thumbnailUrl}
          alt={displayTitle}
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
      ) : (
        // Fallback gradient card for text items or missing thumbnails
        <div
          className="w-full h-full flex items-center justify-center p-3 relative"
          style={{
            background: 'linear-gradient(140deg, #6a4dfd 0%, #9a72ff 40%, #c5a8ff 100%)',
          }}
        >
          {/* Subtle overlay for better text readability */}
          <div className="absolute inset-0 bg-black/5" />
          
          {/* Display first 8-12 words of title */}
          {fallbackText && (
            <p
              className="text-white/90 text-sm text-center leading-relaxed font-medium px-2 relative z-10"
              style={{
                textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                lineHeight: '1.5',
                maxHeight: '100%',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {fallbackText}
            </p>
          )}
        </div>
      )}
    </button>
  );
}

